import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MoreVertical,
  UserCheck,
  CheckCircle2,
  Pencil,
  User,
  UserMinus,
  Bot,
  Store,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useConversationStore } from '@/stores/useConversationStore';
import {
  usePostApiWorkspacesWorkspaceIdConversationsConversationIdAssign,
  usePostApiWorkspacesWorkspaceIdConversationsConversationIdClose,
  usePutApiWorkspacesWorkspaceIdConversationsConversationIdTitle,
  useDeleteApiWorkspacesWorkspaceIdConversationsConversationIdCustomer,
  useGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomer,
  getGetApiWorkspacesWorkspaceIdConversationsQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey,
} from '@/api/generated/conversations/conversations';
import { useQueryClient } from '@tanstack/react-query';
import { MOCK_CONVERSATION_ID } from '../mockData';
import type { ConversationDetail, CustomerDto } from '@/types';
import { cn, getInitials, getCustomerDisplayName } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface ChatHeaderProps {
  conversation: ConversationDetail;
  className?: string;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ conversation, className }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCustomerDrawerOpen, setActiveConversationId } = useConversationStore();
  const { activeWorkspaceId } = useWorkspaceStore();

  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(conversation.title || '');
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);

  const assignMutation = usePostApiWorkspacesWorkspaceIdConversationsConversationIdAssign({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
      },
    },
  });

  const closeMutation = usePostApiWorkspacesWorkspaceIdConversationsConversationIdClose({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
      },
    },
  });

  const updateTitleMutation = usePutApiWorkspacesWorkspaceIdConversationsConversationIdTitle({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
        setIsTitleDialogOpen(false);
      },
    },
  });

  const unlinkCustomerMutation = useDeleteApiWorkspacesWorkspaceIdConversationsConversationIdCustomer({
    mutation: {
      onSuccess: () => {
        queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversation.id), null);
        queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id), (old: any) => 
          old ? { ...old, customerId: null, customerName: null, customerEmail: null, customerPhone: null } : old
        );
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversation.id),
        });
      },
    },
  });

  const handleBackToMobileList = () => {
    setActiveConversationId(null);
    navigate('/conversations');
  };

  const isMock = conversation.id === MOCK_CONVERSATION_ID;

  const handleAssign = () => {
    if (isMock) return;
    assignMutation.mutate({ workspaceId: activeWorkspaceId!, conversationId: conversation.id });
  };

  const handleCloseConfirm = () => {
    if (isMock) return;
    closeMutation.mutate({ workspaceId: activeWorkspaceId!, conversationId: conversation.id });
  };

  const handleUnlinkConfirm = () => {
    if (isMock) return;
    unlinkCustomerMutation.mutate({ workspaceId: activeWorkspaceId!, conversationId: conversation.id });
  };

  const statusStr = String(conversation.status).toUpperCase();
  const isAI = statusStr === 'ACTIVE' || statusStr === 'AI_HANDLING' || conversation.status === 0;
  const isWaiting = statusStr === 'WAITINGFORHUMAN' || statusStr === 'WAITING' || conversation.status === 1;
  const isAssigned = statusStr === 'HUMANASSIGNED' || statusStr === 'HUMAN_ASSIGNED' || conversation.status === 2;
  const isClosed = statusStr === 'CLOSED' || statusStr === 'RESOLVED' || conversation.status === 3;

  const handleSaveTitle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || isClosed || isMock) return;
    updateTitleMutation.mutate({
      workspaceId: activeWorkspaceId!,
      conversationId: conversation.id,
      data: { title: newTitle.trim() },
    });
  };

  const { data: customerData } = useGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomer<CustomerDto>(
    activeWorkspaceId!,
    conversation.id,
    {
      query: {
        enabled: Boolean(conversation.id) && !isMock && !!activeWorkspaceId,
      },
    }
  );

  const rawCustomerName =
    customerData?.name ||
    (customerData as any)?.customerName ||
    conversation.customerName ||
    null;
  const rawCustomerEmail =
    customerData?.email ||
    (customerData as any)?.customerEmail ||
    conversation.customerEmail ||
    null;

  const activeCustomerDisplayName = getCustomerDisplayName(
    rawCustomerName,
    rawCustomerEmail
  );
  const initials = getInitials(rawCustomerName, rawCustomerEmail);

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3 border-b border-border bg-card/90 backdrop-blur-sm shrink-0 min-w-0 z-10',
          className
        )}
      >
        {/* Left Section: Back Button (Mobile) + Customer Info */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
          {/* Mobile back navigation */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToMobileList}
            className="md:hidden h-8 w-8 -ml-1 text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Back to conversations list"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {/* Clickable Customer Avatar & Name (opens CustomerDrawer) */}
          <button
            type="button"
            onClick={() => setCustomerDrawerOpen(true)}
            className="flex items-center gap-3 text-left min-w-0 group hover:opacity-90 transition-opacity rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title="View customer profile details"
          >
            <div className="relative shrink-0">
              <Avatar className="h-9 w-9 border border-border">
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {isAI && (
                <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-violet-600 text-white ring-2 ring-background">
                  <Bot className="h-2 w-2" />
                </span>
              )}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-semibold text-xs sm:text-sm truncate text-foreground group-hover:text-primary transition-colors">
                  {activeCustomerDisplayName}
                </h3>
                <span className="hidden sm:inline-block text-[10px] text-muted-foreground group-hover:text-foreground">
                  (Profile)
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
                <span className="truncate max-w-[200px] sm:max-w-[280px]">
                  {conversation.title || 'Ticket de Suporte Geral'}
                </span>
                {!isClosed && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewTitle(conversation.title || '');
                      setIsTitleDialogOpen(true);
                    }}
                    className="opacity-0 group-hover:opacity-100 hover:text-foreground transition-opacity"
                    aria-label="Edit title"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            </div>
          </button>
        </div>

        {/* Right Section: Badges & Action Controls */}
        <div id="tour-chat-header-actions" className="flex items-center gap-2 shrink-0">
          {/* Status Badge */}
          <span title={isClosed && conversation.closedAtUtc ? `Encerrada em ${new Date(conversation.closedAtUtc).toLocaleString('pt-BR')}` : undefined}>
            <StatusBadge status={String(conversation.status)} className="hidden sm:inline-flex text-[11px]" />
          </span>

          {/* Assigned Agent Badge */}
          {conversation.assignedUserName && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200/50 dark:border-blue-800/50">
              <UserCheck className="h-3 w-3" />
              <span className="truncate max-w-[120px]">{conversation.assignedUserName}</span>
            </span>
          )}

          {/* Store Badge */}
          {conversation.storeName && (
            <span className="hidden lg:inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
              <Store className="h-3 w-3" />
              <span className="truncate max-w-[120px]">{conversation.storeName}</span>
            </span>
          )}

          {/* Context-Aware Primary Action Button */}
          {isAI && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleAssign}
              disabled={assignMutation.isPending}
              className="h-8 text-xs gap-1.5 border-violet-300 text-violet-700 dark:text-violet-300 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/50"
            >
              <UserCheck className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Assumir da IA</span>
              <span className="sm:hidden">Assumir</span>
            </Button>
          )}

          {isWaiting && (
            <Button
              size="sm"
              onClick={handleAssign}
              disabled={assignMutation.isPending}
              className="h-8 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <UserCheck className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Atribuir a Mim</span>
              <span className="sm:hidden">Atribuir</span>
            </Button>
          )}

          {isAssigned && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCloseDialogOpen(true)}
              className="h-8 text-xs gap-1.5 text-foreground hover:bg-muted"
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span className="hidden sm:inline">Fechar Ticket</span>
              <span className="sm:hidden">Fechar</span>
            </Button>
          )}

          {/* Action Menu Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="More actions"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 text-xs">
              <DropdownMenuItem onClick={() => setCustomerDrawerOpen(true)}>
                <User className="h-3.5 w-3.5 mr-2 text-primary" />
                <span>Dados do Cliente</span>
              </DropdownMenuItem>
              {!isClosed && (
                <DropdownMenuItem
                  onClick={() => {
                    setNewTitle(conversation.title || '');
                    setIsTitleDialogOpen(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  <span>Renomear Conversa</span>
                </DropdownMenuItem>
              )}
              {(isAI || isWaiting) && (
                <DropdownMenuItem onClick={handleAssign}>
                  <UserCheck className="h-3.5 w-3.5 mr-2 text-primary" />
                  <span>Assumir Conversa</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {!isClosed && (
                <DropdownMenuItem
                  onClick={() => setIsUnlinkDialogOpen(true)}
                  className="text-amber-600 dark:text-amber-400 focus:text-amber-600"
                >
                  <UserMinus className="h-3.5 w-3.5 mr-2" />
                  <span>Desvincular Cliente</span>
                </DropdownMenuItem>
              )}
              {!isClosed && (
                <DropdownMenuItem
                  onClick={() => setIsCloseDialogOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                  <span>Finalizar Conversa</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Edit Title Dialog */}
      <Dialog open={isTitleDialogOpen} onOpenChange={setIsTitleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleSaveTitle}>
            <DialogHeader>
              <DialogTitle className="text-base font-semibold">Editar Título da Conversa</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Defina um tópico descritivo para esta conversa.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="ex: Solicitação de Devolução #1042"
                className="text-xs"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsTitleDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!newTitle.trim() || updateTitleMutation.isPending}
              >
                {updateTitleMutation.isPending ? 'Salvando...' : 'Salvar Título'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close Conversation Confirmation */}
      <ConfirmDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        title="Resolver e Fechar Conversa"
        description="Tem certeza que deseja marcar esta conversa como resolvida e fechada? O compositor de mensagens será bloqueado."
        confirmLabel="Fechar Conversa"
        variant="destructive"
        onConfirm={handleCloseConfirm}
      />

      {/* Unlink Customer Confirmation */}
      <ConfirmDialog
        open={isUnlinkDialogOpen}
        onOpenChange={setIsUnlinkDialogOpen}
        title="Desvincular Perfil do Cliente"
        description="Tem certeza que deseja desvincular os detalhes do cliente desta conversa? As informações de contato serão redefinidas."
        confirmLabel="Desvincular Cliente"
        variant="destructive"
        onConfirm={handleUnlinkConfirm}
      />
    </>
  );
};

export default ChatHeader;
